/// <reference types="vite/client" />
import { ClientRuntimeConfigSchema, type ClientRuntimeConfig } from '@dank/shared';

let runtimeConfig: ClientRuntimeConfig | null = null;

function optionalEnv(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function getEnvFallbackConfig(): ClientRuntimeConfig {
  return ClientRuntimeConfigSchema.parse({
    useMockAuth: import.meta.env.VITE_USE_MOCK_AUTH === 'true',
    publicBaseUrl: optionalEnv(import.meta.env.VITE_PUBLIC_BASE_URL),
    firebase: {
      apiKey: optionalEnv(import.meta.env.VITE_FIREBASE_API_KEY),
      appId: optionalEnv(import.meta.env.VITE_FIREBASE_APP_ID),
      authDomain: optionalEnv(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
      measurementId: optionalEnv(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID),
      projectId: optionalEnv(import.meta.env.VITE_FIREBASE_PROJECT_ID),
    },
  });
}

function hasUsableFirebaseConfig(config: ClientRuntimeConfig): boolean {
  return Boolean(config.firebase.apiKey && config.firebase.authDomain && config.firebase.projectId);
}

function assertUsableRuntimeConfig(config: ClientRuntimeConfig): ClientRuntimeConfig {
  if (!config.useMockAuth && !hasUsableFirebaseConfig(config)) {
    throw new Error(
      'Runtime config is missing Firebase settings. Set CLIENT_FIREBASE_API_KEY, CLIENT_FIREBASE_AUTH_DOMAIN, and CLIENT_FIREBASE_PROJECT_ID.',
    );
  }
  return config;
}

export function getRuntimeConfig(): ClientRuntimeConfig {
  return assertUsableRuntimeConfig(runtimeConfig ?? getEnvFallbackConfig());
}

export function isMockAuthEnabled(): boolean {
  return getRuntimeConfig().useMockAuth;
}

export async function loadRuntimeConfig(): Promise<ClientRuntimeConfig> {
  if (runtimeConfig) return runtimeConfig;

  try {
    const response = await fetch('/api/runtime-config', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Runtime config request failed with status ${response.status}`);
    }

    runtimeConfig = assertUsableRuntimeConfig(
      ClientRuntimeConfigSchema.parse(await response.json()),
    );
    return runtimeConfig;
  } catch (error) {
    const fallbackConfig = getEnvFallbackConfig();
    if (fallbackConfig.useMockAuth || hasUsableFirebaseConfig(fallbackConfig)) {
      runtimeConfig = assertUsableRuntimeConfig(fallbackConfig);
      return runtimeConfig;
    }

    console.error('Unable to load runtime configuration', error);
    throw new Error('Unable to load runtime configuration.');
  }
}

export function setRuntimeConfigForTests(nextConfig: ClientRuntimeConfig | null): void {
  runtimeConfig = nextConfig;
}
